import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, DatePicker, Row, Select, Tooltip, Form } from "antd";
import moment from "moment";
import { find } from "lodash";
import { v4 as uuid } from "uuid";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import "../../styles/row.less";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import ChangeVersionsTable from "../common/ChangeVersionsTable";

const styles = {
  buttonIcon: {
    boxShadow: "none",
    border: 0,
  },
  input: {
    width: "300px",
  },
  currentVersion: {
    fontWeight: "bold",
  },
};

export default function AccountInfoVersionsTable(props) {
  const { closeAccountVersion, changeAccountVersion, getAccountVersions } = props;
  const {
    accountsVersions,
    accountsVersionsLoading,
    accountStatus,
    accountStatusLoading,
  } = useSelector((state) => state.accounts);
  const { accountStatuses, accountTypes } = useSelector((state) => state.dicts);
  const currentDate = moment();
  const [childTableData, setChildTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  
  const [form] = Form.useForm();

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      form.setFieldsValue(field);
    }, 0);
  };

  const [currentRec, setCurrentRec] = useState({});

  useEffect(() => {
    if (checkIsLoaded(accountStatusLoading)) {
      setTableData([
        {
          key: accountStatus.id,
          id: accountStatus.id,
          accountStatusId: accountStatus.accountStatusId,
          fromDate: accountStatus.fromDate,
          toDate: accountStatus.toDate,
          accTypeId: accountStatus.accTypeId,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountStatusLoading]);

  useEffect(() => {
    if (checkIsLoaded(accountsVersionsLoading)) {
      setChildTableData(
        accountsVersions[0]
          .map((version, i) => ({
            key: `${version.id}${version.fromDate}`,
            id: version.id,
            accountStatusId: version.accountStatusId,
            fromDate: version.fromDate,
            toDate: version.toDate,
            properties: version.properties,
            isCurrentVersion:
              moment(currentDate).isSameOrAfter(version.fromDate) &&
              moment(currentDate).isBefore(version.toDate),
            isPastVersion: moment(version?.toDate).isBefore(currentDate),
            isLatestVersion: accountsVersions[0].length === i + 1,
            isClosing: false,
            isCreated: false,
            accTypeId: version.accTypeId,
          }))
          .sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsVersionsLoading]);

  const handleCreateVersion = ({ fromDate, accountStatusId, accTypeId }) => {
    const createdVersion = childTableData.find((item) => item.isCreated);
    const date = fromDate.isSame(createdVersion.fromDate, "day")
      ? toHttpFormat(moment(createdVersion.fromDate).add(1, "second"))
      : toHttpFormatStartOfDay(fromDate);

    changeAccountVersion(accountStatusId, date, accTypeId);
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    accountStatusId: 400,
    accTypeId: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Статус",
      dataIndex: "accountStatusId",
      key: "accountStatusId",
      width: columnsWidths.accountStatusId,
      render: (value, row) => (
        <span style={row.isChild && row.isCurrentVersion ? styles.currentVersion : null}>
          {find(accountStatuses, { id: row.accountStatusId })?.name}
        </span>
      ),
    },
    {
      title: "Тип аккаунта",
      dataIndex: "accTypeId",
      key: "accTypeId",
      width: columnsWidths.accTypeId,
      render: (value, row) => (
        <span style={row.isChild && row.isCurrentVersion ? styles.currentVersion : null}>
          {find(accountTypes, { id: row.accTypeId })?.name}
        </span>
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render(text, record) {
        return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
      },
    },
    {
      title: "",
      dataIndex: "actions",
      width: columnsWidths.actions,
      key: "actions",
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Статус",
      dataIndex: "accountStatusId",
      key: "accountStatusId",
      width: columnsWidths.accountStatusId,
      render: (value, record) => (
        <ChangeVersionsTable.Select
          dic={accountStatuses}
          name="accountStatusId"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
          presetAddVerionValue
        />
      ),
    },
    {
      title: "Тип аккаунта",
      dataIndex: "accTypeId",
      key: "accTypeId",
      width: columnsWidths.accTypeId,
      render: (value, record) => (
        <ChangeVersionsTable.Select
          dic={accountTypes}
          name="accTypeId"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
          presetAddVerionValue
        />
      ),
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      render(text, record) {
        if (record.isCreated) {
          handleSetInitialValue({ fromDate: moment(record.fromDate).add(1, "day") });
          return (
            <>
              <Form.Item
                name="fromDate"
                className="form-item-required__hidden-error__mb0"
                rules={[{ required: true }]}
                style={{ display: "inline-block", marginRight: "5px" }}
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) =>
                    current < moment(record.fromDate) || current > moment(record.toDate)
                  }
                  // defaultPickerValue={moment(record.fromDate).add(1, "day")}
                />
              </Form.Item>
              <Form.Item
                style={{ display: "inline-block" }}
                className="form-item-required__hidden-error__mb0"
              >
                -{toDisplayFormat(record.toDate)}
              </Form.Item>
            </>
          );
        }
        if (record.isClosing) {
          handleSetInitialValue({ toDate: moment(record.toDate) });
          return (
            <>
              <Form.Item
                style={{ display: "inline-block", marginRight: "5px" }}
                className="form-item-required__hidden-error__mb0"
              >
                {toDisplayFormat(record.fromDate)}-
              </Form.Item>
              <Form.Item
                name="toDate"
                className="form-item-required__hidden-error__mb0"
                rules={[{ required: true }]}
                style={{ display: "inline-block" }}
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) => current < moment().startOf("month")}
                  // defaultPickerValue={moment(record.toDate)}
                />
              </Form.Item>
            </>
          );
        }
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      render: (value, record) => {
        if (hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE)) {
          if (!record.isPastVersion && !record.isCreated && !record.isClosing) {
            const addVersion = (e) => {
              e.stopPropagation();
              const newVersion = {
                ...record,
                key: uuid(),
                fromDate: moment(record.fromDate).add(1, "seconds"),
                isCreated: true,
                isCurrentVersion: false,
                isPastVersion: false,
                isLatestVersion: false,
              };
              setCurrentRec(newVersion);
              setChildTableData((prevState) =>
                [newVersion, ...prevState].sort((a, b) =>
                  moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1
                )
              );
            };

            return (
              <Row>
                {!moment(record.fromDate)
                  .startOf("day")
                  .isSame(moment(record.toDate).startOf("day")) && (
                  <Tooltip title="Добавить версию">
                    <Button
                      className="button-icon"
                      icon={<AddVersionIcon />}
                      type="text"
                      htmlType="button"
                      disabled={isChangingForbidden}
                      onClick={addVersion}
                    />
                  </Tooltip>
                )}
              </Row>
            );
          }
          if (record.isCreated || record.isClosing) {
            const handleDiscard = (e) => {
              e.stopPropagation();
              setCurrentRec({});
              setChildTableData((prevState) => prevState.filter((obj) => !obj.isCreated));
              form.resetFields();
            };
            return (
              <Row>
                <Tooltip title="Подтвердить">
                  <Button
                    className="button-icon"
                    icon={<SubmitButtonIcon />}
                    type="text"
                    htmlType="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      form.submit();
                    }}
                  />
                </Tooltip>
                <Tooltip title="Отменить">
                  <Button
                    className="button-icon"
                    icon={<CancelButtonIcon />}
                    type="text"
                    htmlType="button"
                    onClick={handleDiscard}
                  />
                </Tooltip>
              </Row>
            );
          }
          return null;
        }
        return null;
      },
    },
    {
      title: "",
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const handleSubmit = (values) => {
    if (currentRec.isCreated) {
      handleCreateVersion(values);
    } else if (currentRec.isClosing) {
      closeAccountVersion(currentRec.id, toHttpFormatEndOfDay(values.toDate));
    }
    setCurrentRec({});
  };

  return (
    <Form onFinish={handleSubmit} form={form}>
      <ToggleableVersionsTable
        tableName="organization-status-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={checkIsLoading(accountsVersionsLoading)}
        tableClassName="organization-status-table"
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={() => {
          getAccountVersions();
        }}
      />
    </Form>
  );
}
